import Constants from '../utils/constants';
import Utils from '../utils/utils';
import * as Formulas from '../utils/formulas';
import { DAC_HAS_INTERNAL_INPUT_AND_TOPOLOGY_INPUT_IS_BUFFERED } from '../utils/validations';

const SUPPORTED_PSTS = Constants.SUPPORTED_PSTS;
const SIGNAL_CHAIN_ITEMS = Constants.SIGNAL_CHAIN_ITEMS;
const DACS = Constants.DACS;
const TOPOLOGY_INPUTS = Constants.TOPOLOGY_INPUTS;
const TOPOLOGY_OPTIONS = Constants.TOPOLOGY_OPTIONS;
const CDP_PARAMETERS = Constants.CDP.PARAMETERS;
const RESISTORS = Constants.RESISTORS;

/**
 * Warning Functions that can be applied to each dac type
 */
let warn = {
  whenDacHasVrefPosIntBuffAndTopologyIsNonInvAmpWithBuff: (model) => {
    return DAC_HAS_INTERNAL_INPUT_AND_TOPOLOGY_INPUT_IS_BUFFERED(
      CDP_PARAMETERS.HAS_VREF_POS_INTERNAL_BUFFER,
      model.signalChainHandler.dac.selected,
      model.topologyInputHandler.current.find((input) => input.config.id === TOPOLOGY_INPUTS.NON_INVERTING),
      'messages.pos_int_buff',
    );
  },
  whenDacHasVrefNegIntBufferAndTopologyIsInvAmpWithBuff: (model) => {
    return DAC_HAS_INTERNAL_INPUT_AND_TOPOLOGY_INPUT_IS_BUFFERED(
      CDP_PARAMETERS.HAS_VREF_NEG_INTERNAL_BUFFER,
      model.signalChainHandler.dac.selected,
      model.topologyInputHandler.current.find((input) => input.config.id === TOPOLOGY_INPUTS.INVERTING),
      'messages.neg_int_buff',
    );
  },
  whenDacHasIntOutputAmpAndTopologyOutputIsBuffer: (model) => {
    return DAC_HAS_INTERNAL_INPUT_AND_TOPOLOGY_INPUT_IS_BUFFERED(
      CDP_PARAMETERS.HAS_INTERNAL_OUTPUT_AMPLIFIER,
      model.signalChainHandler.dac.selected,
      model.topologyInputHandler.current.find((input) => input.config.id === TOPOLOGY_INPUTS.OUTPUT),
      'messages.int_output_buff',
    );
  },
};

/**
 * This information tells us what cdp data to get
 */
const psts = {
  voltageReference: {
    id: SUPPORTED_PSTS.VOLTAGE_REFERENCE,
    isInSignalChain: true,
    supportedParameters: ['initialAccuracyMax', 'voutTempcoMaxppmC', 'lineRegulationMaxppmV', 'loadRegulationppmmA', 'voutV', 'vnoiseTyp', 'temperature'],
    pstDisplayColumnWidths: [0, 0, 0, 0, 0, 0, 0, 0], // Part Number always first item, the rest match up to the supportedParameters
  },
  operationalAmplifiers: {
    id: SUPPORTED_PSTS.OPERATIONAL_AMP,
    isInSignalChain: true,
    supportedParameters: ['vosMaxV', 'vosTCMax', 'cmrr', 'ibiasTyp', 'iosMaxA', 'openLoopVoltageGain', 'temperature'],
    pstDisplayColumnWidths: [0, 0, 0, 0, 0, 0, 0, 0], // Part Number always first item, the rest match up to the supportedParameters
  },
  voltageOutputDac: {
    id: SUPPORTED_PSTS.VOLTAGE_OUTPUT_DAC,
    supportedParameters: ['resolution', 'dacVoutInlMax', 'dacVOffseterror', 'dacTotalUnadjustedError', 'offsetVoltageErrorPpm', 'vrefLevelInternal', 'VrefExternal', 'vrefSource', 'vrefTc', 'gainErrorPercentFsMax', 'vNoiseDensity', 'temperature'],
    pstDisplayColumnWidths: [130, 100, 100, 150, 200, 185, 140, 110, 150, 100, 130, 120, 110], // Part Number always first item, the rest match up to the supportedParameters
  },
  multiplyingDac: {
    id: SUPPORTED_PSTS.MULTIPLYING_DAC,
    unsupportedParts: [
      'AD7564',
      'AD7943',
      'AD7945',
      'AD7948',
      'AD7568',
      'AD766',
      'DAC8143',
      'DAC8043',
      'DAC8221',
      'DAC8222',
      'DAC8248',
      'AD7538',
      'AD7628',
      'AD7547',
      'DAC8408',
      'AD7549',
      'AD667S',
      'AD7534',
      'AD7548',
      'AD567',
      'AD7111A',
      'AD7528',
      'DAC312',
      'DAC10',
      'AD7542',
      'AD565S',
      'AD7524',
      'AD7533',
      'AD561S',
      'DAC08',
      'DAC08S',
      'DAC100S',
    ],
    supportedParameters: ['resolution', 'dacVoutInlMax', 'dacVOffseterror', 'dacTotalUnadjustedError', 'offsetVoltageErrorPpm', 'dacMultiplyingBandwidth', 'gainErrorPercentFsMax', 'temperature', 'vNoiseDensity'],
    supportPartsForParameters: ['vNoiseDensity', 'dacMultiplyingBandwidth', 'inputResistance', 'internalResistors', 'referenceResistor', 'refResistorMatch', 'feedbackResistor'], // The part is supported if it has data for any of these parameters
    pstDisplayColumnWidths: [0, 0, 0, 0, 0, 0, 0, 0, 0, 0], // Part Number always first item, the rest match up to the supportedParameters
  },
  currentSourceDac: {
    id: SUPPORTED_PSTS.CURRENT_SOURCE_DAC,
    supportedParts: ['AD5423', 'AD5421', 'AD5420', 'AD5412', 'AD5422', 'AD5410'],
    supportedParameters: ['tueIoutTyp', 'vrefSource', 'temperature'],
    pstDisplayColumnWidths: [0, 0, 150, 0], // Part Number always first item, the rest match up to the supportedParameters
  },
  currentSourceSinkDac: {
    id: SUPPORTED_PSTS.CURRENT_SOURCE_SINK_DAC,
    supportedParts: ['AD5770R', 'LTC2672', 'LTC2662-12', 'LTC2662-16', 'LTC2672-12', 'LTC2672-16'],
    supportedParameters: ['tueIoutTyp', 'vrefSource', 'temperature'],
    pstDisplayColumnWidths: [0, 0, 150, 0], // Part Number always first item, the rest match up to the supportedParameters
  },
  fastPrecisionDac: {
    id: SUPPORTED_PSTS.FAST_PRECISION_DAC,
    supportedParts: ['AD3541R', 'AD3542R', 'AD3551R', 'AD3552R'],
    supportedParameters: ['dacType', 'numberOfChannels', 'resolution', 'dacUpdateRate', 'sfdrdbTyp', 'iOutFsMax', 'dataInputInterface', 'temperature'],
    pstDisplayColumnWidths: [0, 290, 0, 0, 0, 0, 0, 0, 0], // Part Number always first item, the rest match up to the supportedParameters
  },
};

const signalChainItems = {
  voltageReference: {
    id: SIGNAL_CHAIN_ITEMS.VOLTAGE_REFERENCE,
    pst: psts.voltageReference,
    name: 'voltage-reference',
    isVoltageReference: true,
  },
  internalVoltageReference: {
    id: SIGNAL_CHAIN_ITEMS.INTERNAL_VOLTAGE_REFERENCE,
    pst: psts.voltageReference,
    name: 'internal-voltage-reference',
    isVoltageReference: true,
    isInternal: true,
    useSelectedDac: true,
    noPartSelection: true,
    noHighlightGroup: true,
  },
  invertingAmplifier: {
    id: SIGNAL_CHAIN_ITEMS.INVERTING_AMP,
    pst: psts.operationalAmplifiers,
    name: 'inverting-amplifier',
    resistor: RESISTORS.INVERTING,
    isInverting: true,
    article: 'an',
  },
  invertingBuffer: {
    id: SIGNAL_CHAIN_ITEMS.INVERTING_BUFFER,
    pst: psts.operationalAmplifiers,
    name: 'inverting-buffer',
    isBuffer: true,
    article: 'an',
  },
  nonInvertingAmplifier: {
    id: SIGNAL_CHAIN_ITEMS.NON_INVERTING_AMP,
    pst: psts.operationalAmplifiers,
    name: 'non-inverting-amplifier',
    isNonInverting: true,
    resistor: RESISTORS.NON_INVERTING,
  },
  nonInvertingBuffer: {
    id: SIGNAL_CHAIN_ITEMS.NON_INVERTING_BUFFER,
    pst: psts.operationalAmplifiers,
    name: 'non-inverting-buffer',
    isBuffer: true,
  },
  outputAmplifier: {
    id: SIGNAL_CHAIN_ITEMS.OUTPUT_AMP,
    pst: psts.operationalAmplifiers,
    name: 'output-amplifier',
    resistor: RESISTORS.OUTPUT,
    article: 'an',
  },
  externalRset: {
    id: SIGNAL_CHAIN_ITEMS.EXTERNAL_RSET,
    pst: psts.operationalAmplifiers,
    name: 'external-rset',
    resistor: RESISTORS.EXTERNAL_RSET,
    isInverting: true,
    article: 'an',
  },
  outputBuffer: {
    id: SIGNAL_CHAIN_ITEMS.OUTPUT_BUFFER,
    pst: psts.operationalAmplifiers,
    name: 'output-buffer',
    isBuffer: true,
    article: 'an',
  },
  buffer: {
    id: SIGNAL_CHAIN_ITEMS.BUFFER,
    pst: psts.operationalAmplifiers,
    name: 'buffer',
    isBuffer: true,
  },
};

let defaultVoltageReferenceFn = (params) => {
  if (!(params && params.selectedDac)) return 'LTC6655B-5';
  let validVrefList = [
      { name: 'ADR4525B', value: 2.5 },
      { name: 'LTC6655B-5', value: 5 },
      { name: 'ADR01', value: 10 },
      { name: 'ADR4530B', value: 3 },
      { name: 'ADR4540B', value: 4.096 },
      { name: 'LTC6655B-1.25', value: 1.25 },
      { name: 'ADR4520B', value: 2.048 },
    ], // order matters here
    selectedDac = params.selectedDac,
    vrefExternal = Formulas.GET_CDP_NUMERIC_VALUE(Constants.CDP.PARAMETERS.VREF_EXTERNAL_TYP, selectedDac).value,
    value1 = Formulas.GET_CDP_NUMERIC_VALUE(Constants.CDP.PARAMETERS.VREF_POS_EXTERNAL.MIN, selectedDac).value,
    value2 = Formulas.GET_CDP_NUMERIC_VALUE(Constants.CDP.PARAMETERS.VREF_POS_EXTERNAL.MAX, selectedDac).value,
    result = null;

  if (Utils.isNumeric(value1) && Utils.isNumeric(value2)) {
    let minValue = Math.min(value1, value2),
      maxValue = Math.max(value1, value2);

    for (const item of validVrefList) {
      if (item.value >= minValue && item.value <= maxValue) {
        result = item.name;
        break; // break out of the loop, we have found the result
      }
    }
  }

  if (!result && Utils.isNumeric(vrefExternal)) {
    let foundVrefExternal = validVrefList.find((item) => item.value === vrefExternal);
    if (foundVrefExternal) result = foundVrefExternal.name;
  }

  if (
    !result &&
    (!Utils.isNumeric(value1) || !Utils.isNumeric(value2)) &&
    (Utils.isNumeric(value1) || Utils.isNumeric(value2)) &&
    !Utils.isNumeric(vrefExternal)
  ) {
    let foundVrefExternal = validVrefList.find((item) => item.value === Math.floor(value1 || value2));
    if (foundVrefExternal) result = foundVrefExternal.name;
  }

  if (!result && !Utils.isNumeric(value1) && !Utils.isNumeric(value2) && !Utils.isNumeric(vrefExternal))
    result = 'LTC6655B-5';

  if (!result) {
    console.warn(
      `A default Voltage Reference could not be found for this configuration: dac: ${selectedDac.name}; min: ${value1}; max: ${value2}; vref external: ${vrefExternal}`,
    );
    result = 'ADR01';
  }

  return result;
};

let multiplyingDacSignalChainItems = [];
multiplyingDacSignalChainItems[0] = Object.assign({}, signalChainItems.voltageReference, { default: 'ADR4550B' });
multiplyingDacSignalChainItems[1] = Object.assign({}, signalChainItems.invertingAmplifier, { default: 'AD8603' });
multiplyingDacSignalChainItems[2] = Object.assign({}, signalChainItems.buffer, { default: 'AD8603' });
multiplyingDacSignalChainItems[3] = Object.assign({}, signalChainItems.outputAmplifier, {
  default: 'AD8676',
  isInverting: true,
});

let voltageOutputDacSignalChainItems = [];
voltageOutputDacSignalChainItems[0] = Object.assign({}, signalChainItems.voltageReference, {
  defaultFn: defaultVoltageReferenceFn,
});
voltageOutputDacSignalChainItems[1] = Object.assign({}, signalChainItems.internalVoltageReference);
voltageOutputDacSignalChainItems[2] = Object.assign({}, signalChainItems.nonInvertingAmplifier, {
  default: 'ADA4077-1',
});
voltageOutputDacSignalChainItems[3] = Object.assign({}, signalChainItems.nonInvertingBuffer, { default: 'AD8676' });
voltageOutputDacSignalChainItems[4] = Object.assign({}, signalChainItems.invertingAmplifier, { default: 'ADA4077-1' });
voltageOutputDacSignalChainItems[5] = Object.assign({}, signalChainItems.invertingBuffer, { default: 'AD8676' });
voltageOutputDacSignalChainItems[6] = Object.assign({}, signalChainItems.outputAmplifier, {
  default: 'AD8675',
  isNonInverting: true,
});
voltageOutputDacSignalChainItems[7] = Object.assign({}, signalChainItems.outputBuffer, { default: 'AD8675' });

let currentSourceSignalChainItems = [];
currentSourceSignalChainItems.push(
  Object.assign({}, signalChainItems.voltageReference, { defaultFn: defaultVoltageReferenceFn }),
);
currentSourceSignalChainItems.push(Object.assign({}, signalChainItems.internalVoltageReference));
currentSourceSignalChainItems.push(Object.assign({}, signalChainItems.externalRset, { noPartSelection: true }));

let currentSourceSinkSignalChainItems = [];
currentSourceSinkSignalChainItems.push(
  Object.assign({}, signalChainItems.voltageReference, { default: 'LTC6655B-1.25' }),
);
currentSourceSinkSignalChainItems.push(Object.assign({}, signalChainItems.internalVoltageReference));
currentSourceSinkSignalChainItems.push(Object.assign({}, signalChainItems.externalRset, { noPartSelection: true }));

let fastPrecisionSignalChainItems = [];
fastPrecisionSignalChainItems.push(Object.assign({}, signalChainItems.voltageReference, { default: 'ADR4525B' }));
fastPrecisionSignalChainItems.push(Object.assign({}, signalChainItems.internalVoltageReference));
fastPrecisionSignalChainItems.push(Object.assign({}, signalChainItems.nonInvertingAmplifier, { default: 'ADA4077-1' }));
fastPrecisionSignalChainItems.push(Object.assign({}, signalChainItems.nonInvertingBuffer, { default: 'AD8676' }));
fastPrecisionSignalChainItems.push(
  Object.assign({}, signalChainItems.outputAmplifier, {
    default: 'AD8065',
    isNonInverting: true,
  }),
);
fastPrecisionSignalChainItems.push(Object.assign({}, signalChainItems.outputBuffer, { default: 'AD8675' }));

const amplifierGain = {
  nonInverting: {
    default: 2,
    fn: (r3, r4) => 1 + r4 / r3,
  },
  inverting: {
    default: -1,
    fn: (r1, r2) => -r2 / r1,
  },
  output: {
    default: null,
    fn: () => null,
  },
};

const resistors = {
  inverting: {
    id: RESISTORS.INVERTING,
    name: 'inverting-amplifier',
    article: 'an',
    reset: {
      tolerance: 0.01,
      tempco: 5,
      res1: 5000,
      res2: 10000,
    },
    isInSecondaryVoltagePath: true,
    isPositive: false,
    suppressTempCo: false,
    amplifierGainMetadata: amplifierGain.inverting,
  },
  rset: {
    id: RESISTORS.EXTERNAL_RSET,
    name: 'external-rset', // translation related
    article: 'an',
    reset: {
      tolerance: 0.01,
      tempco: 5,
      // res1: 4700,
      res2: 4700,
    },
    res1Type: 'empty_string', // translation related again
    res2Type: 'just_r', // desired result: no text
    isResistorDisabled: true,
    hideRes1: true,
    isPositive: true,
    suppressTempCo: false,
    requiresGainValidation: false,
    isInPrimaryVoltagePath: true,
    findResistorNetworkPart: false,
  },
  nonInverting: {
    id: RESISTORS.NON_INVERTING,
    name: 'non-inverting-amplifier',
    reset: {
      tolerance: 0.01,
      tempco: 5,
      res1: 10000,
      res2: 10000,
    },
    res1Type: 'r3',
    res2Type: 'r4',
    suppressTempCo: false,
    isInPrimaryVoltagePath: true,
    amplifierGainMetadata: amplifierGain.nonInverting,
  },
  output: {
    id: RESISTORS.OUTPUT,
    name: 'output-amplifier',
    article: 'an',
    reset: {
      tolerance: 0.01,
      tempco: 5, //2,
      res1: 10000,
      res2: 10000,
    },
    res1Type: 'r5',
    res2Type: 'r6',
    suppressTempCo: false,
    requiresGainValidation: false,
    amplifierGainMetadata: amplifierGain.output,
  },
  externalInverting: {
    id: RESISTORS.INVERTING,
    name: 'inverting-buffer-external',
    article: 'an',
    isDisabled: true,
    useDefaultResistorLabel: false,
    isInPrimaryVoltagePath: true,
    isPositive: false,
    reset: {
      res1: 10000,
      res2: 10000,
      tolerance: 0.01,
      tempco: 5,
    },
    suppressTempCo: false,
    hasInternalParamsKey: CDP_PARAMETERS.INVERTING_RESISTORS.HAS_INTERNAL,
    internalParamKey: CDP_PARAMETERS.INVERTING_RESISTORS.INTERNAL,
    toleranceParamKey: CDP_PARAMETERS.INVERTING_RESISTORS.TOLERANCE,
    tempcoParamKey: CDP_PARAMETERS.INVERTING_RESISTORS.TEMPCO,
    amplifierGainMetadata: amplifierGain.inverting,
  },
  internalOutput: {
    id: RESISTORS.OUTPUT,
    name: 'output-amplifier-internal',
    article: 'an',
    res1Type: 'r5',
    res2Type: 'r6',
    requiresGainValidation: false,
    isShown: false,
    isAlwaysInternal: true,
    defaults: {
      res1: Constants.DEFAULTS.OUTPUT_RESISTOR_VALUE,
      res2: Constants.DEFAULTS.OUTPUT_RESISTOR_VALUE,
    },
    hasInternalParamsKey: null, // this is always internal
    internalParamKey: CDP_PARAMETERS.OUTPUT_RESISTORS.INTERNAL,
    amplifierGainMetadata: amplifierGain.output,
  },
};

const dacs = [
  {
    id: DACS.VOLTAGE_OUTPUT,
    pst: psts.voltageOutputDac,
    name: 'voltage-output-dac',
    analyticsName: 'Vout',
    defaultsSelected: {
      dac: 'AD5791',
      computed: [{ propertyName: 'operatingTemperature', defaultLocation: 'selectedTemperatureRange' }],
      computedOverrides: [
        {
          propertyName: 'operatingTemperature',
          overrideFn: (value) => {
            if (value.min === -55) value.min = -40;
            return value;
          },
        },
      ],
    },
    defaults: {
      powerSupplyRipple: 0.001,
      calibrationToggled: false,
    },
    resistors: [resistors.inverting, resistors.nonInverting, resistors.output],
    topologyInputs: [
      { id: TOPOLOGY_INPUTS.VOLTAGE_REFERENCE, overrides: {} },
      { id: TOPOLOGY_INPUTS.NON_INVERTING, overrides: {} },
      { id: TOPOLOGY_INPUTS.INVERTING, overrides: {} },
      { id: TOPOLOGY_INPUTS.OUTPUT, overrides: {} },
    ],
    signalChainItems: voltageOutputDacSignalChainItems,
    overrideSvgForDacs: ['AD5791'],
    warnings: [
      warn.whenDacHasVrefPosIntBuffAndTopologyIsNonInvAmpWithBuff,
      warn.whenDacHasVrefNegIntBufferAndTopologyIsInvAmpWithBuff,
      warn.whenDacHasIntOutputAmpAndTopologyOutputIsBuffer,
    ],
  },
  {
    id: DACS.MULTIPLYING,
    pst: psts.multiplyingDac,
    name: 'multiplying-dac',
    analyticsName: 'MDAC',
    defaultsSelected: {
      dac: 'AD5453',
      computed: [{ propertyName: 'operatingTemperature', defaultLocation: 'selectedTemperatureRange' }],
    },
    defaults: {
      powerSupplyRipple: 0.001,
      calibrationToggled: false,
    },
    resistors: [resistors.externalInverting, resistors.internalOutput],
    isSinglePath: true,
    topologyInputs: [{ id: TOPOLOGY_INPUTS.MULTIPLYING_DAC, overrides: {} }],
    signalChainItems: multiplyingDacSignalChainItems,
    overrideSvgForDacs: [],
  },
  {
    id: DACS.CURRENT_SOURCE,
    pst: psts.currentSourceDac,
    name: 'current-source-dac',
    topologySvgName: 'IoutVoutDac',
    analyticsName: 'Iout',
    defaultsSelected: {
      dac: 'AD5421',
      computed: [{ propertyName: 'operatingTemperature', defaultLocation: 'selectedTemperatureRange' }],
    },
    defaults: {
      powerSupplyRipple: 0.001,
      calibrationToggled: false,
    },
    resistors: [
      // Controls the third panel's inputs
      resistors.rset,
    ],
    topologyInputs: [
      // Controls the pulldowns
      { id: TOPOLOGY_INPUTS.VOLTAGE_REFERENCE, overrides: {} }, // Is already "external or internal"
      { id: TOPOLOGY_INPUTS.IOUT_OR_VOUT, overrides: { isShown: false } },
      { id: TOPOLOGY_INPUTS.RSET, overrides: {} },
    ],
    signalChainItems: currentSourceSignalChainItems,
    overrideSvgForDacs: [],
  },
  {
    id: DACS.CURRENT_SOURCE_SINK, // create an identifier to distinguish this dac type from another dac type
    pst: psts.currentSourceSinkDac, // pst configuration created above - This configures how we are accessing the CDP data for this type
    name: 'current-source-sink-dac', // create an identifier that can be used to access files, styling identifiers, localization
    topologySvgName: 'IoutVoutDac',
    analyticsName: 'Iout', // How we want to reference this dac type in analytics
    defaultsSelected: {
      dac: 'AD5770R', // The default selected DAC
      computed: [{ propertyName: 'operatingTemperature', defaultLocation: 'selectedTemperatureRange' }], // Defaults that are computed based on the selected DAC - default location is the computed property on the DAC
    },
    defaults: {
      powerSupplyRipple: 0.001,
      calibrationToggled: false,
    }, // Static defaults for user input fields in the application parameters tab
    resistors: [resistors.rset], // Tells us which resistors are "available"
    topologyInputs: [
      { id: TOPOLOGY_INPUTS.VOLTAGE_REFERENCE, overrides: {} }, // Is already "external or internal"
      { id: TOPOLOGY_INPUTS.IOUT_OR_VOUT, overrides: { isShown: false } },
      { id: TOPOLOGY_INPUTS.RSET, overrides: { isShown: false, default: TOPOLOGY_OPTIONS.INTERNAL } },
      { id: TOPOLOGY_INPUTS.OUTPUT_RANGE, overrides: {} },
    ], // Tells us which pulldowns we want to put in the topology svg
    signalChainItems: currentSourceSinkSignalChainItems, // Tells us which signal chain items are "available"
    overrideSvgForDacs: [], // Determines which DACs should have a special topology svg - the svg then needs to include the DAC name in the SVG name
  },

  {
    id: DACS.FAST_PRECISION, // create an identifier to distinguish this dac type from another dac type
    pst: psts.fastPrecisionDac, // pst configuration created above - This configures how we are accessing the CDP data for this type
    name: 'fast-precision-dac', // create an identifier that can be used to access files, styling identifiers, localization
    topologySvgName: 'FastPrecisionDac',
    analyticsName: 'FastPrecisionDac', // How we want to reference this dac type in analytics
    defaultsSelected: {
      dac: 'AD3552R', // The default selected DAC
      computed: [{ propertyName: 'operatingTemperature', defaultLocation: 'selectedTemperatureRange' }], // Defaults that are computed based on the selected DAC - default location is the computed property on the DAC
    },
    defaults: {
      powerSupplyRipple: 0.001,
      calibrationToggled: false,
    }, // Static defaults for user input fields in the application parameters tab
    resistors: [resistors.nonInverting], // Tells us which resistors are "available"
    topologyInputs: [
      { id: TOPOLOGY_INPUTS.VOLTAGE_REFERENCE, overrides: {} }, // Is already "external or internal"
      { id: TOPOLOGY_INPUTS.FP_DAC_TYPE, overrides: {} },
      { id: TOPOLOGY_INPUTS.FP_NON_INVERTING, overrides: {} },
      { id: TOPOLOGY_INPUTS.IOUT_OR_VOUT, overrides: { isShown: false } },
      { id: TOPOLOGY_INPUTS.FP_DAC_OUTPUT, overrides: {} },
    ], // Tells us which pulldowns we want to put in the topology svg
    signalChainItems: fastPrecisionSignalChainItems, // Tells us which signal chain items are "available"
    overrideSvgForDacs: [], // Determines which DACs should have a special topology svg - the svg then needs to include the DAC name in the SVG name
  },
];

export default {
  dacs: dacs,
  psts: psts,
};
